import { cn } from "./classNames-BzrkDnOD.js";
import { useState } from "react";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
import { diffLines, diffWords } from "diff";
//#region src/components/DiffView/DiffViewMark.tsx
const variants$1 = cva([
	"tw:p-0.5",
	"tw:text-inherit",
	"tw:inline-flex"
], { variants: { diff: {
	added: ["tw:bg-green-200", "tw:[[data-theme=dark]_&]:bg-green-500/40"],
	removed: ["tw:bg-error-300", "tw:[[data-theme=dark]_&]:bg-error-500/40"]
} } });
const DiffViewMark = ({ diff, children, className, ...props }) => /* @__PURE__ */ jsx("mark", {
	...props,
	className: cn(variants$1({
		diff,
		className
	})),
	children
});
//#endregion
//#region src/components/DiffView/DiffViewTd.tsx
const variants = cva([
	"tw:[&_pre]:p-0",
	"tw:[&_pre]:m-0",
	"tw:[&_pre]:border-none",
	"tw:[&_pre]:rounded-none",
	"tw:align-top",
	"tw:[&_pre]:bg-transparent",
	"tw:[&_pre]:text-inherit",
	"tw:[&_pre]:px-2",
	"tw:[&_pre]:!leading-7",
	"tw:[&_pre]:text-sm",
	"tw:[&_pre]:whitespace-pre-wrap"
], {
	defaultVariants: {
		variant: "default",
		diff: "default"
	},
	variants: {
		variant: {
			default: [],
			gutter: [
				"tw:w-14",
				"tw:text-right",
				"tw:whitespace-nowrap",
				"tw:select-none"
			],
			code: []
		},
		diff: {
			default: [],
			added: [],
			removed: []
		}
	},
	compoundVariants: [
		{
			variant: "gutter",
			diff: "default",
			className: ["tw:bg-muted", "tw:text-muted-foreground"]
		},
		{
			variant: "gutter",
			diff: "added",
			className: ["tw:bg-green-200 tw:text-green-900", "tw:[[data-theme=dark]_&]:bg-green-500/25 tw:[[data-theme=dark]_&]:text-green-300"]
		},
		{
			variant: "gutter",
			diff: "removed",
			className: ["tw:bg-error-200 tw:text-error-700", "tw:[[data-theme=dark]_&]:bg-error-500/25 tw:[[data-theme=dark]_&]:text-error-300"]
		},
		{
			variant: "code",
			diff: "default",
			className: ["tw:bg-background", "tw:text-foreground"]
		},
		{
			variant: "code",
			diff: "added",
			className: ["tw:bg-green-100 tw:text-foreground", "tw:[[data-theme=dark]_&]:bg-green-500/15"]
		},
		{
			variant: "code",
			diff: "removed",
			className: ["tw:bg-error-100 tw:text-foreground", "tw:[[data-theme=dark]_&]:bg-error-500/15"]
		}
	]
});
const DiffViewTd = ({ diff, variant = "default", children, className, ...props }) => {
	const needsCodeFormatting = variant === "code" || variant === "gutter";
	return /* @__PURE__ */ jsx("td", {
		...props,
		className: cn(variants({
			variant,
			className,
			diff
		})),
		children: needsCodeFormatting ? /* @__PURE__ */ jsx("pre", { children }) : children
	});
};
//#endregion
//#region src/components/DiffView/DiffViewTh.tsx
const DiffViewTh = ({ variant = "default", children, className, ...props }) => /* @__PURE__ */ jsx("th", {
	...props,
	className: cn("tw:bg-background", "tw:py-3", "tw:border-b", "tw:border-border", { "tw:w-14": variant === "gutter" }, { "tw:last:pr-8": variant === "default" }, className),
	children
});
//#endregion
//#region src/components/DiffView/util.ts
const DiffType = {
	DEFAULT: "default",
	ADDED: "added",
	REMOVED: "removed"
};
const EXTRA_LINES = 3;
const TAIL_BLOCK = Number.POSITIVE_INFINITY;
const splitLines = (value) => {
	if (value === "") return [];
	return value.replace(/\n$/, "").split("\n");
};
const computeWordDiff = (oldValue, newValue) => {
	const result = {
		left: [],
		right: []
	};
	diffWords(oldValue, newValue).forEach(({ added, removed, value }) => {
		if (added) {
			result.right.push({
				type: DiffType.ADDED,
				value,
				lineNumber: 0
			});
			return;
		}
		if (removed) {
			result.left.push({
				type: DiffType.REMOVED,
				value,
				lineNumber: 0
			});
			return;
		}
		const unchanged = {
			type: DiffType.DEFAULT,
			value,
			lineNumber: 0
		};
		result.right.push(unchanged);
		result.left.push(unchanged);
	});
	return result;
};
const findPairedAddedLine = (diffArray, blockIndex, lineIndex) => {
	const nextBlock = diffArray[blockIndex + 1];
	if (!nextBlock?.added) return null;
	return splitLines(nextBlock.value)[lineIndex] ?? null;
};
const buildUnchangedLine = (value, counters) => {
	counters.left += 1;
	counters.right += 1;
	return {
		left: {
			type: DiffType.DEFAULT,
			value,
			lineNumber: counters.left
		},
		right: {
			type: DiffType.DEFAULT,
			value,
			lineNumber: counters.right
		}
	};
};
const buildAddedLine = (value, counters) => {
	counters.right += 1;
	return {
		left: {},
		right: {
			type: DiffType.ADDED,
			value,
			lineNumber: counters.right
		}
	};
};
const buildRemovedLine = (value, counters) => {
	counters.left += 1;
	return {
		left: {
			type: DiffType.REMOVED,
			value: value || " ",
			lineNumber: counters.left
		},
		right: {}
	};
};
const buildModifiedLine = (leftValue, rightValue, counters) => {
	counters.left += 1;
	counters.right += 1;
	if ((leftValue || " ") === rightValue) return {
		line: {
			left: {
				type: DiffType.DEFAULT,
				value: rightValue,
				lineNumber: counters.left
			},
			right: {
				type: DiffType.DEFAULT,
				value: rightValue,
				lineNumber: counters.right
			}
		},
		isActualChange: false
	};
	const { left, right } = computeWordDiff(leftValue, rightValue);
	return {
		line: {
			left: {
				type: DiffType.REMOVED,
				value: left,
				lineNumber: counters.left
			},
			right: {
				type: DiffType.ADDED,
				value: right,
				lineNumber: counters.right
			}
		},
		isActualChange: true
	};
};
const computeLineInformation = (oldString, newString, ignoreWhitespace) => {
	const diffArray = diffLines(oldString.trimEnd(), newString.trimEnd(), { ignoreWhitespace });
	const counters = {
		left: 0,
		right: 0,
		global: 0
	};
	const lineInformation = [];
	const diffLineSet = /* @__PURE__ */ new Set();
	const consumedAddedLines = /* @__PURE__ */ new Set();
	diffArray.forEach((block, blockIndex) => {
		splitLines(block.value).forEach((value, lineIndex) => {
			if (consumedAddedLines.has(`${blockIndex}-${lineIndex}`)) return;
			if (block.removed) {
				const paired = findPairedAddedLine(diffArray, blockIndex, lineIndex);
				if (paired !== null) {
					consumedAddedLines.add(`${blockIndex + 1}-${lineIndex}`);
					const { line, isActualChange } = buildModifiedLine(value, paired, counters);
					lineInformation.push(line);
					if (isActualChange) diffLineSet.add(counters.global);
				} else {
					lineInformation.push(buildRemovedLine(value, counters));
					diffLineSet.add(counters.global);
				}
			} else if (block.added) {
				lineInformation.push(buildAddedLine(value, counters));
				diffLineSet.add(counters.global);
			} else lineInformation.push(buildUnchangedLine(value, counters));
			counters.global += 1;
		});
	});
	return {
		lineInformation,
		diffLines: [...diffLineSet].sort((a, b) => a - b)
	};
};
const toSegments = (lineInformation, diffLineSet) => {
	const segments = [];
	let current = null;
	lineInformation.forEach((line, index) => {
		const isChange = diffLineSet.has(index);
		if (current === null || current.kind === "change" !== isChange) {
			current = isChange ? {
				kind: "change",
				lines: [line],
				firstIndex: index
			} : {
				kind: "context",
				lines: [line],
				firstIndex: index
			};
			segments.push(current);
		} else current.lines.push(line);
	});
	return segments;
};
const normalizeSide = (side) => {
	const raw = Array.isArray(side.value) ? side.value : [{
		value: side.value,
		type: DiffType.DEFAULT,
		lineNumber: 0
	}];
	return {
		type: side.type,
		lineNumber: side.lineNumber,
		words: raw.filter((w) => typeof w.value === "string")
	};
};
const renderLineRow = (line) => ({
	kind: "line",
	left: normalizeSide(line.left),
	right: normalizeSide(line.right),
	key: `${line.left.lineNumber}-${line.right.lineNumber}`
});
const renderContextSegment = (segment, nextChangeIndex, hasPrevChange, hasNextChange, expanded) => {
	if (expanded.has(nextChangeIndex)) return segment.lines.map(renderLineRow);
	const total = segment.lines.length;
	const trail = hasPrevChange ? Math.min(EXTRA_LINES, total) : 0;
	let lead = hasNextChange ? Math.min(EXTRA_LINES, total - trail) : 0;
	let foldCount = total - trail - lead;
	if (!hasNextChange && foldCount === 1) {
		foldCount = 0;
		lead = 1;
	}
	const rows = [];
	if (trail > 0) rows.push(...segment.lines.slice(0, trail).map(renderLineRow));
	if (foldCount > 0) rows.push({
		kind: "fold",
		blockStart: nextChangeIndex,
		lineCount: foldCount,
		key: hasNextChange ? `fold-${nextChangeIndex}` : `fold-tail-${segment.firstIndex + total - 1}`
	});
	if (lead > 0) rows.push(...segment.lines.slice(trail + foldCount).map(renderLineRow));
	return rows;
};
const computeDiffRows = (lineInformation, diffLines, expandedBlocks) => {
	const diffLineSet = new Set(diffLines);
	const expanded = new Set(expandedBlocks);
	const segments = toSegments(lineInformation, diffLineSet);
	const rows = [];
	let hasPrevChange = false;
	segments.forEach((segment, i) => {
		if (segment.kind === "change") {
			rows.push(...segment.lines.map(renderLineRow));
			hasPrevChange = true;
			return;
		}
		const nextChange = segments.slice(i + 1).find((s) => s.kind === "change");
		const nextStart = nextChange?.firstIndex ?? TAIL_BLOCK;
		rows.push(...renderContextSegment(segment, nextStart, hasPrevChange, !!nextChange, expanded));
	});
	return rows;
};
//#endregion
//#region src/components/DiffView/DiffView.tsx
const DiffView = ({ leftTitle, leftTitleGutter, rightTitle, rightTitleGutter, oldValue, newValue, ignoreWhitespace, className }) => {
	const [expandedBlocks, setExpandedBlocks] = useState([]);
	const { lineInformation, diffLines } = computeLineInformation(oldValue, newValue, ignoreWhitespace);
	const rows = computeDiffRows(lineInformation, diffLines, expandedBlocks);
	const handleExpand = (blockNumber) => {
		setExpandedBlocks((curr) => [...curr, blockNumber]);
	};
	return /* @__PURE__ */ jsx("div", {
		className: cn("tailwindcss-preflight", "tw:bg-muted", "tw:text-foreground", "tw:border", "tw:border-border", "tw:rounded-2xl", "tw:overflow-auto", className),
		children: /* @__PURE__ */ jsxs("table", {
			className: "tw:w-full tw:table-fixed tw:bg-background",
			children: [/* @__PURE__ */ jsx("thead", {
				className: "tw:sticky tw:top-0 tw:z-10 tw:shadow-sm",
				children: /* @__PURE__ */ jsxs("tr", { children: [
					/* @__PURE__ */ jsx(DiffViewTh, {
						variant: "gutter",
						children: leftTitleGutter
					}),
					/* @__PURE__ */ jsx(DiffViewTh, { children: leftTitle }),
					/* @__PURE__ */ jsx(DiffViewTh, {
						variant: "gutter",
						children: rightTitleGutter
					}),
					/* @__PURE__ */ jsx(DiffViewTh, { children: rightTitle })
				] })
			}), /* @__PURE__ */ jsx("tbody", { children: rows.map((row) => {
				if (row.kind === "fold") return /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx(DiffViewTd, {
					colSpan: 4,
					children: /* @__PURE__ */ jsxs("button", {
						type: "button",
						onClick: () => handleExpand(row.blockStart),
						className: cn("tw:w-full", "tw:text-center", "tw:bg-muted", "tw:text-foreground", "tw:hover:bg-accent", "tw:underline", "tw:p-2"),
						children: [
							"Expand ",
							row.lineCount,
							" lines ..."
						]
					})
				}) }, row.key);
				return /* @__PURE__ */ jsxs("tr", { children: [
					/* @__PURE__ */ jsx(DiffViewTd, {
						variant: "gutter",
						diff: row.left.type,
						children: row.left.lineNumber
					}),
					/* @__PURE__ */ jsx(DiffViewTd, {
						variant: "code",
						diff: row.left.type,
						children: row.left.words.map((val, index) => {
							if (val.type === "default") return val.value;
							return /* @__PURE__ */ jsx(DiffViewMark, {
								diff: val.type,
								children: val.value
							}, `${val.value}-${val.type}-${index}`);
						})
					}),
					/* @__PURE__ */ jsx(DiffViewTd, {
						variant: "gutter",
						diff: row.right.type,
						children: row.right.lineNumber
					}),
					/* @__PURE__ */ jsx(DiffViewTd, {
						variant: "code",
						diff: row.right.type,
						children: row.right.words.map((val, index) => {
							if (val.type === "default") return val.value;
							return /* @__PURE__ */ jsx(DiffViewMark, {
								diff: val.type,
								children: val.value
							}, `${val.value}-${val.type}-${index}`);
						})
					})
				] }, row.key);
			}) })]
		})
	});
};
//#endregion
export { DiffType, DiffView, computeDiffRows, computeLineInformation };

//# sourceMappingURL=DiffView-ppTIzh2h.js.map